import React, { useState } from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Typography, Box, Collapse, Tooltip,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  Dashboard, LocationOn, Assessment, Settings,
  ExpandLess, Map, MonitorHeart, Security,
  LocalGasStation, Analytics, Devices, LocalShipping,
  Logout, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import Logo from '../../../assets/Logo.png';

interface SidebarItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  path: string;
  children?: SidebarItem[];
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  currentPath?: string;
  variant?: 'permanent' | 'temporary';
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const SIDEBAR_WIDTH           = 224;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

/* ── Dark sidebar palette ── */
const NAVY        = '#0F172A';
const NAVY_HOVER  = 'rgba(255,255,255,0.06)';
const NAVY_BORDER = 'rgba(255,255,255,0.07)';
const ACCENT      = 'rgba(99,155,230,1)';
const ACCENT_SOFT = 'rgba(99,155,230,0.15)';
const TEXT_PRI    = '#F1F5F9';
const TEXT_SEC    = 'rgba(255,255,255,0.45)';

const sidebarItems: SidebarItem[] = [
  { id: 'dashboard',  title: 'Dashboard',        icon: <Dashboard />,    path: '/dashboard' },
  { id: 'analytics',  title: 'Analytics',         icon: <Analytics />,    path: '/analytics' },
  {
    id: 'monitoring', title: 'Monitoring',         icon: <MonitorHeart />, path: '/monitoring',
    children: [
      { id: 'fuel-quality', title: 'Fuel Quality',     icon: <LocalGasStation />, path: '/monitoring/fuel' },
      { id: 'security',     title: 'Security & Theft', icon: <Security />,        path: '/security' },
    ],
  },
  {
    id: 'sites',      title: 'Sites & Locations',  icon: <LocationOn />,   path: '/sites',
    children: [
      { id: 'site-map', title: 'Site Map',       icon: <Map />,           path: '/sites/map' },
      { id: 'tankers',  title: 'Tanker Fleet',   icon: <LocalShipping />, path: '/sites/tankers' },
    ],
  },
  { id: 'devices',   title: 'Device Management', icon: <Devices />,      path: '/devices' },
  { id: 'reports',   title: 'Reports',            icon: <Assessment />,   path: '/reports' },
  { id: 'settings',  title: 'Settings',           icon: <Settings />,     path: '/settings' },
];

const NavItem: React.FC<{
  item: SidebarItem;
  level: number;
  currentPath?: string;
  expanded: string[];
  onToggleExpand: (id: string) => void;
  onNavigate?: (path: string) => void;
  collapsed?: boolean;
}> = ({ item, level, currentPath, expanded, onToggleExpand, onNavigate, collapsed }) => {
  const hasChildren   = !!item.children?.length;
  const isChildActive = hasChildren ? item.children!.some(c => currentPath === c.path) : false;
  const isActive      = currentPath === item.path || isChildActive;
  const isExpanded    = expanded.includes(item.id) || isChildActive;

  if (collapsed) {
    return (
      <ListItem disablePadding sx={{ mb: 0.25 }}>
        <Tooltip title={item.title} placement="right" arrow>
          <ListItemButton
            onClick={() => onNavigate?.(item.path)}
            sx={{
              mx: 'auto', width: 40, height: 40, minWidth: 'unset',
              borderRadius: '10px', justifyContent: 'center',
              bgcolor: isActive ? ACCENT_SOFT : 'transparent',
              border: `1px solid ${isActive ? 'rgba(99,155,230,0.3)' : 'transparent'}`,
              transition: 'all 0.15s',
              '&:hover': { bgcolor: isActive ? ACCENT_SOFT : NAVY_HOVER },
            }}
          >
            <ListItemIcon sx={{ minWidth: 'unset', color: isActive ? ACCENT : TEXT_SEC, '& svg': { fontSize: 19 } }}>
              {item.icon}
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  }

  return (
    <>
      <ListItem disablePadding sx={{ mb: 0.25 }}>
        <ListItemButton
          onClick={() => hasChildren ? onToggleExpand(item.id) : onNavigate?.(item.path)}
          sx={{
            mx: 1, px: level > 0 ? 1.5 : 1.25, pl: level > 0 ? 3.5 : 1.25,
            py: 0.75, minHeight: 40, borderRadius: '10px',
            bgcolor: isActive ? ACCENT_SOFT : 'transparent',
            borderLeft: `3px solid ${isActive ? ACCENT : 'transparent'}`,
            transition: 'all 0.15s',
            '&:hover': { bgcolor: isActive ? ACCENT_SOFT : NAVY_HOVER },
          }}
        >
          <ListItemIcon sx={{ minWidth: 34, color: isActive ? ACCENT : TEXT_SEC, '& svg': { fontSize: level === 0 ? 19 : 17 } }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.title}
            sx={{
              '& .MuiTypography-root': {
                fontSize: level === 0 ? '0.875rem' : '0.82rem',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? ACCENT : TEXT_PRI,
              },
            }}
          />
          {hasChildren && (
            <Box sx={{ color: TEXT_SEC, display: 'flex', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
              <ExpandLess sx={{ fontSize: 16 }} />
            </Box>
          )}
        </ListItemButton>
      </ListItem>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children!.map(child => (
              <NavItem key={child.id} item={child} level={level + 1}
                currentPath={currentPath} expanded={expanded}
                onToggleExpand={onToggleExpand} onNavigate={onNavigate} collapsed={false}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const SidebarContent: React.FC<{
  currentPath?: string;
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showToggle?: boolean;
}> = ({ currentPath, onNavigate, onLogout, collapsed = false, onToggleCollapse, showToggle = true }) => {
  const [expanded, setExpanded] = useState<string[]>([]);

  const toggleExpand = (id: string) =>
    setExpanded(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: NAVY, overflow: 'hidden' }}>

      {/* Brand */}
      <Box sx={{
        position: 'relative',
        px: collapsed ? 0 : 2.5, pt: 2, pb: 1.5,
        borderBottom: `1px solid ${NAVY_BORDER}`,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: collapsed ? 'center' : 'flex-start',
      }}>
        {collapsed ? (
          <>
            <Box component="img" src={Logo} alt="PetroTag360"
              sx={{ height: 36, objectFit: 'contain', mb: 1.5, filter: 'brightness(0) invert(1)' }}
            />
            <Tooltip title="Expand sidebar" placement="right" arrow>
              <Box
                onClick={onToggleCollapse}
                sx={{
                  width: 26, height: 26, borderRadius: '7px',
                  bgcolor: 'rgba(255,255,255,0.08)', border: `1px solid ${NAVY_BORDER}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.15s',
                  '&:hover': { bgcolor: ACCENT_SOFT },
                }}
              >
                <ChevronRight sx={{ fontSize: 16, color: TEXT_SEC }} />
              </Box>
            </Tooltip>
          </>
        ) : (
          <>
            {showToggle && (
              <Tooltip title="Collapse sidebar" placement="right" arrow>
                <Box
                  onClick={onToggleCollapse}
                  sx={{
                    position: 'absolute', top: 10, right: 10,
                    width: 26, height: 26, borderRadius: '7px',
                    bgcolor: 'rgba(255,255,255,0.08)', border: `1px solid ${NAVY_BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.15s', zIndex: 1,
                    '&:hover': { bgcolor: ACCENT_SOFT },
                  }}
                >
                  <ChevronLeft sx={{ fontSize: 16, color: TEXT_SEC }} />
                </Box>
              </Tooltip>
            )}

            <Box component="img" src={Logo} alt="PetroTag360"
              sx={{ height: 72, objectFit: 'contain', objectPosition: 'left center', maxWidth: '85%', mb: 1,
                    filter: 'brightness(0) invert(1)' }}
            />
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: ACCENT, letterSpacing: '0.05em' }}>
              By ITSA
            </Typography>
            <Typography sx={{ fontSize: '0.64rem', color: TEXT_SEC, mt: 0.25, lineHeight: 1.5 }}>
              Complete control across every site and supply chain
            </Typography>
          </>
        )}
      </Box>

      {/* Nav */}
      <Box sx={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1.5,
        px: collapsed ? 1.5 : 0,
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2 },
      }}>
        <List disablePadding>
          {sidebarItems.map(item => (
            <NavItem key={item.id} item={item} level={0}
              currentPath={currentPath} expanded={expanded}
              onToggleExpand={toggleExpand} onNavigate={onNavigate}
              collapsed={collapsed}
            />
          ))}
        </List>
      </Box>

      {/* Quick Stats */}
      {!collapsed && (
        <Box sx={{ flexShrink: 0, px: 2, pt: 1.5, pb: 1, borderTop: `1px solid ${NAVY_BORDER}`,
                   bgcolor: 'rgba(0,0,0,0.2)' }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
                            textTransform: 'uppercase', color: TEXT_SEC, mb: 1.25 }}>
            Quick Stats
          </Typography>
          {[
            { label: 'Active Sites',       value: '05', color: '#4ADE80' },
            { label: 'Running Generators', value: '02', color: '#60A5FA' },
            { label: 'Active Alerts',      value: '02', color: '#F87171' },
          ].map(stat => (
            <Box key={stat.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.875 }}>
              <Typography sx={{ fontSize: '0.8rem', color: TEXT_PRI }}>{stat.label}</Typography>
              <Box sx={{ px: 1.25, py: 0.15, borderRadius: '20px',
                         bgcolor: `${stat.color}18`, border: `1px solid ${stat.color}40` }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: stat.color }}>{stat.value}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Logout */}
      <Box sx={{ p: collapsed ? 1.5 : 2, borderTop: `1px solid ${NAVY_BORDER}`, flexShrink: 0 }}>
        {collapsed ? (
          <Tooltip title="Log out" placement="right" arrow>
            <Box onClick={onLogout}
              sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                width: 40, height: 40, mx: 'auto', borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.06)', cursor: 'pointer', transition: 'all 0.15s',
                '&:hover': { bgcolor: 'rgba(239,68,68,0.15)' },
              }}
            >
              <Logout sx={{ fontSize: 17, color: TEXT_SEC }} />
            </Box>
          </Tooltip>
        ) : (
          <Box onClick={onLogout}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              px: 1.5, py: 1, borderRadius: '10px',
              bgcolor: 'rgba(255,255,255,0.06)', cursor: 'pointer', transition: 'all 0.15s',
              '&:hover': { bgcolor: 'rgba(239,68,68,0.15)' },
            }}
          >
            <Logout sx={{ fontSize: 17, color: TEXT_SEC }} />
            <Typography sx={{ fontSize: '0.855rem', fontWeight: 500, color: TEXT_PRI }}>Log out</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  open, onClose, currentPath, onNavigate, onLogout,
  collapsed = false, onToggleCollapse,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  if (!isMobile) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0, left: 0,
        width,
        height: '100vh',
        bgcolor: NAVY,
        borderRight: `1px solid ${NAVY_BORDER}`,
        zIndex: theme.zIndex.drawer,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width 0.22s ease',
      }}>
        <SidebarContent
          currentPath={currentPath}
          onNavigate={onNavigate}
          onLogout={onLogout}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          showToggle
        />
      </Box>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          bgcolor: NAVY,
        },
      }}
    >
      <SidebarContent
        currentPath={currentPath}
        onNavigate={onNavigate}
        onLogout={onLogout}
        collapsed={false}
        showToggle={false}
      />
    </Drawer>
  );
};

export default Sidebar;
